import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { useAuth, API } from '../App';
import axios from 'axios';
import { Button } from './ui/button';
import { Textarea } from './ui/textarea';
import { toast } from 'sonner';
import { 
  X, Sparkles, Palette, Eye, Heart, Sun, ChevronRight, 
  Loader2, Check, ArrowRight, Image, Crown, Star
} from 'lucide-react';

const ArtisticDirectionModal = ({ 
  isOpen, 
  onClose, 
  onGenerated,
  existingIdentity 
}) => {
  const { token } = useAuth();
  const navigate = useNavigate();
  const [currentStep, setCurrentStep] = useState(0); // 0 = intro, 1-5 = questions
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  
  // Form data for the 5 questions
  const [answers, setAnswers] = useState({
    visualFeeling: '',
    brandAdjectives: '',
    avoidElements: '',
    inspirationBrands: '',
    clientPerception: ''
  });

  const questions = [
    {
      key: 'visualFeeling',
      icon: Eye,
      title: "Quelle émotion veux-tu transmettre visuellement ?",
      placeholder: "Ex: Élégance et confiance, chaleur et authenticité, dynamisme et créativité...",
      hint: "Pense à ce que tu veux que tes clientes ressentent quand elles découvrent ta marque"
    },
    {
      key: 'brandAdjectives',
      icon: Heart,
      title: "Si ta marque était une personne, comment serait-elle ?",
      placeholder: "Ex: Sophistiquée mais accessible, audacieuse mais bienveillante...",
      hint: "3 à 5 adjectifs qui définissent la personnalité de ta marque"
    },
    {
      key: 'avoidElements',
      icon: X,
      title: "Qu'est-ce que tu veux absolument éviter ?",
      placeholder: "Ex: Pas de rose trop vif, pas de style trop corporate, pas d'images génériques...",
      hint: "Tout ce qui ne te ressemble pas ou qui te met mal à l'aise"
    },
    {
      key: 'inspirationBrands',
      icon: Sun,
      title: "Quelles marques t'inspirent visuellement ?",
      placeholder: "Ex: Chanel pour l'élégance, Apple pour la simplicité, Anthropologie pour la chaleur...",
      hint: "Ce ne sont pas des concurrentes, mais des références d'univers"
    },
    {
      key: 'clientPerception',
      icon: Palette,
      title: "Comment veux-tu que tes clientes perçoivent ta marque ?",
      placeholder: "Ex: Premium et exclusive, accessible et chaleureuse, innovante et moderne...",
      hint: "La première impression que tu veux créer"
    }
  ];

  const handleAnswerChange = (key, value) => {
    setAnswers(prev => ({ ...prev, [key]: value }));
  };

  const isCurrentStepValid = () => {
    if (currentStep === 0) return true; // Intro step
    const currentQuestion = questions[currentStep - 1];
    return answers[currentQuestion.key]?.trim().length >= 10;
  };

  const handleNext = () => {
    if (currentStep < 5) {
      setCurrentStep(prev => prev + 1);
    } else {
      generateArtisticDirection();
    }
  };

  const handleBack = () => {
    if (currentStep > 0) {
      setCurrentStep(prev => prev - 1);
    }
  };

  const generateArtisticDirection = async () => {
    setLoading(true);
    try {
      const headers = { Authorization: `Bearer ${token}` };
      const response = await axios.post(`${API}/vision-identity/generate-artistic-direction`, {
        answers,
        existing_identity: existingIdentity
      }, { headers });
      
      setResult(response.data);
      toast.success('Ta direction artistique est prête !');
    } catch (error) {
      console.error('Error generating artistic direction:', error);
      toast.error('Une erreur est survenue. Réessaie.');
    } finally {
      setLoading(false);
    }
  };

  const handleComplete = () => {
    if (onGenerated && result) {
      onGenerated(result.artistic_direction);
    }
    onClose();
  };

  const handleGoToMoodboard = () => {
    if (onGenerated && result) {
      onGenerated(result.artistic_direction);
    }
    onClose();
    navigate('/vision-board-generator');
  };

  if (!isOpen) return null;

  const currentQuestion = currentStep > 0 ? questions[currentStep - 1] : null;
  const Icon = currentQuestion?.icon;

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/90 backdrop-blur-sm"
      >
        <motion.div
          initial={{ opacity: 0, scale: 0.95, y: 20 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          exit={{ opacity: 0, scale: 0.95, y: 20 }}
          className="w-full max-w-2xl bg-[#0A0A0A] border border-[#D4AF37]/30 overflow-hidden max-h-[90vh] overflow-y-auto"
        >
          {/* Header */}
          <div className="p-6 border-b border-[#1C1C1C]">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-[#D4AF37] text-xs font-accent tracking-wider mb-1">
                  DIRECTION ARTISTIQUE
                </p>
                <h2 className="font-serif text-2xl text-white">
                  {result ? '✨ Ton univers prend vie' : 'Définis ton univers visuel'}
                </h2>
              </div>
              <Button
                variant="ghost"
                size="icon"
                onClick={onClose}
                className="text-[#52525B] hover:text-white"
              >
                <X size={20} />
              </Button>
            </div>
            
            {/* Progress bar - only show during questions */}
            {!result && currentStep > 0 && (
              <div className="mt-4 flex gap-2">
                {[1, 2, 3, 4, 5].map((step) => (
                  <div
                    key={step}
                    className={`h-1 flex-1 transition-colors ${
                      step <= currentStep ? 'bg-[#D4AF37]' : 'bg-[#27272A]'
                    }`}
                  />
                ))}
              </div>
            )}
          </div>

          {/* Content */}
          <div className="p-8">
            {loading ? (
              /* Loading State */
              <div className="text-center py-12">
                <Loader2 className="w-12 h-12 text-[#D4AF37] animate-spin mx-auto mb-4" />
                <p className="text-white font-serif text-xl mb-2">
                  Création de ton univers visuel...
                </p>
                <p className="text-[#A1A1AA] text-sm">
                  L'AI analyse tes réponses pour créer ta direction artistique unique
                </p>
              </div>
            ) : result ? (
              /* WOW Result View */
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                className="space-y-6"
              >
                {/* Success Header */}
                <div className="text-center mb-8">
                  <motion.div
                    initial={{ scale: 0 }}
                    animate={{ scale: 1 }}
                    transition={{ type: "spring", delay: 0.2 }}
                    className="w-16 h-16 mx-auto mb-4 bg-gradient-to-br from-[#D4AF37] to-[#B8860B] rounded-full flex items-center justify-center"
                  >
                    <Crown className="text-black" size={32} />
                  </motion.div>
                  <h3 className="font-serif text-xl text-white mb-2">
                    Tu viens de définir une direction visuelle puissante
                  </h3>
                  <p className="text-[#A1A1AA] text-sm">
                    Alignée avec qui tu es vraiment
                  </p>
                </div>

                {/* Direction Artistique */}
                <div className="bg-[#050505] border border-[#D4AF37]/20 p-6">
                  <p className="text-[#D4AF37] text-xs font-accent tracking-wider mb-3">
                    TON UNIVERS VISUEL
                  </p>
                  <p className="text-white leading-relaxed font-light">
                    {result.artistic_direction}
                  </p>
                </div>

                {/* Visual Keywords as Style Tags */}
                {result.visual_keywords && (
                  <div>
                    <p className="text-[#D4AF37] text-xs font-accent tracking-wider mb-3">
                      🎨 TON STYLE
                    </p>
                    <div className="flex flex-wrap gap-2">
                      {result.visual_keywords.map((keyword, index) => (
                        <motion.span
                          key={index}
                          initial={{ opacity: 0, scale: 0.8 }}
                          animate={{ opacity: 1, scale: 1 }}
                          transition={{ delay: 0.3 + index * 0.1 }}
                          className="px-4 py-2 bg-[#D4AF37]/10 border border-[#D4AF37]/30 text-[#E5C585] text-sm"
                        >
                          {keyword}
                        </motion.span>
                      ))}
                    </div>
                  </div>
                )}

                {/* Mood/Energy */}
                {result.mood && (
                  <div className="bg-gradient-to-r from-[#D4AF37]/10 to-transparent border-l-2 border-[#D4AF37] p-4">
                    <p className="text-[#52525B] text-xs mb-1">🖤 TON ÉNERGIE</p>
                    <p className="text-white font-serif text-lg">{result.mood}</p>
                  </div>
                )}

                {/* Identity Statement - The WOW moment */}
                <motion.div
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.5 }}
                  className="text-center py-6 border-t border-b border-[#1C1C1C]"
                >
                  <p className="text-[#D4AF37] text-xs font-accent tracking-wider mb-3">
                    👑 QUI TU DEVIENS
                  </p>
                  <p className="text-white font-serif text-lg italic">
                    "Une femme qui impose son image avec intention, sans jamais se trahir."
                  </p>
                </motion.div>

                {/* Next Step CTA */}
                <div className="space-y-3 pt-4">
                  <p className="text-[#52525B] text-xs text-center mb-4">
                    🎯 PROCHAINE ÉTAPE : Transformer cet univers en visuels concrets
                  </p>
                  
                  <Button
                    onClick={handleGoToMoodboard}
                    className="w-full btn-gold py-4"
                    data-testid="go-to-moodboard-btn"
                  >
                    <Image size={18} className="mr-2" />
                    Créer mon univers visuel
                    <ArrowRight size={18} className="ml-2" />
                  </Button>
                  
                  <Button
                    onClick={handleComplete}
                    variant="ghost"
                    className="w-full text-[#52525B] hover:text-[#A1A1AA]"
                  >
                    Revenir plus tard
                  </Button>
                </div>
              </motion.div>
            ) : currentStep === 0 ? (
              /* Intro Screen */
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                className="text-center py-8"
              >
                <div className="w-20 h-20 mx-auto mb-6 bg-[#D4AF37]/10 border border-[#D4AF37]/30 rounded-full flex items-center justify-center">
                  <Sparkles className="text-[#D4AF37]" size={36} />
                </div>
                
                <h3 className="font-serif text-2xl text-white mb-4">
                  En 3 minutes, tu vas définir<br />
                  <span className="text-[#D4AF37]">l'univers visuel de ta marque</span>
                </h3>
                
                <p className="text-[#A1A1AA] mb-8 max-w-md mx-auto">
                  Je vais te poser 5 questions simples. Réponds avec sincérité — 
                  il n'y a pas de bonne ou mauvaise réponse, juste TOI.
                </p>

                <div className="grid grid-cols-3 gap-4 mb-8 text-center">
                  <div className="p-4 bg-[#050505] border border-[#1C1C1C]">
                    <p className="text-[#D4AF37] font-serif text-2xl mb-1">5</p>
                    <p className="text-[#52525B] text-xs">Questions</p>
                  </div>
                  <div className="p-4 bg-[#050505] border border-[#1C1C1C]">
                    <p className="text-[#D4AF37] font-serif text-2xl mb-1">3</p>
                    <p className="text-[#52525B] text-xs">Minutes</p>
                  </div>
                  <div className="p-4 bg-[#050505] border border-[#1C1C1C]">
                    <p className="text-[#D4AF37] font-serif text-2xl mb-1">1</p>
                    <p className="text-[#52525B] text-xs">Direction unique</p>
                  </div>
                </div>

                <Button
                  onClick={handleNext}
                  className="btn-gold px-8 py-4"
                >
                  <Star size={18} className="mr-2" />
                  Commencer
                  <ChevronRight size={18} className="ml-2" />
                </Button>
              </motion.div>
            ) : (
              /* Question Form */
              <motion.div
                key={currentStep}
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -20 }}
                className="space-y-6"
              >
                <div className="flex items-center gap-4 mb-6">
                  <div className="w-12 h-12 flex items-center justify-center bg-[#D4AF37]/10 border border-[#D4AF37]/30">
                    {Icon && <Icon className="text-[#D4AF37]" size={24} />}
                  </div>
                  <div>
                    <p className="text-[#52525B] text-xs mb-1">Question {currentStep}/5</p>
                    <h3 className="text-white font-medium text-lg">{currentQuestion.title}</h3>
                  </div>
                </div>

                <Textarea
                  value={answers[currentQuestion.key]}
                  onChange={(e) => handleAnswerChange(currentQuestion.key, e.target.value)}
                  placeholder={currentQuestion.placeholder}
                  className="w-full h-32 bg-[#050505] border-[#27272A] text-white placeholder:text-[#52525B] focus:border-[#D4AF37]/50"
                />

                <p className="text-[#52525B] text-xs italic">
                  💡 {currentQuestion.hint}
                </p>

                <div className="flex gap-4">
                  <Button
                    onClick={handleBack}
                    variant="outline"
                    className="flex-1 border-[#27272A] text-[#A1A1AA] hover:bg-[#1C1C1C]"
                  >
                    Précédent
                  </Button>
                  <Button
                    onClick={handleNext}
                    disabled={!isCurrentStepValid()}
                    className="flex-1 btn-gold"
                  >
                    {currentStep === 5 ? (
                      <>
                        <Sparkles size={18} className="mr-2" />
                        Générer ma direction
                      </>
                    ) : (
                      <>
                        Suivant
                        <ChevronRight size={18} className="ml-2" />
                      </>
                    )}
                  </Button>
                </div>
              </motion.div>
            )}
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
};

export default ArtisticDirectionModal;
